import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Radio, Select, Switch, message } from 'antd';
import { fetchPostTags, updatePost, getPostErrorMessage } from '@/apis/posts';
import ImagePicker from '@/components/imagepicker';
import PostOptions, { postOptionsPayload } from '@/components/postoptions';
import ImageQuota from '@/components/imagequota';
import PostFormSection from '@/components/postform';
import { fetchPostImageQuota } from '@/apis/posts';
import { uploadSelectedImages } from '@/apis/uploads';
import './PostEditor.scss';

const toLocalTime = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const PostEditor = ({ post, onSaved, onCancel, onBusyChange }) => {
  const postId = post.id;
  const [form] = Form.useForm();
  const recruitingValue = Form.useWatch('is_recruiting', form);
  const isRecruiting = recruitingValue ?? post.is_recruiting;
  const [files, setFiles] = useState([]);
  const links = Form.useWatch('images', form) || (post.images || []).map(image => image.image_url);
  const [tags, setTags] = useState(post.tags || []);
  const [tagsError, setTagsError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mapBusy, setMapBusy] = useState(false);
  const busyRef = useRef(false);
  useEffect(() => {
    let active = true;
    fetchPostTags().then(result => { if (active) setTags(result); })
      .catch(error => { if (active) setTagsError(getPostErrorMessage(error, '标签加载失败，保留当前标签')); });
    return () => { active = false; };
  }, []);
  const perform = async operation => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); onBusyChange(true);
    try { await operation(); }
    catch (error) { message.error(getPostErrorMessage(error, '保存失败，请稍后重试')); }
    finally { busyRef.current = false; setBusy(false); onBusyChange(false); }
  };
  const save = values => !mapBusy && perform(async () => {
    const data = {
      title: values.title.trim(), description: values.description.trim(),
      is_recruiting: values.is_recruiting,
      ...(values.is_recruiting ? { location: values.location?.trim() || '', recruitment_count: values.recruitment_count, ...postOptionsPayload(values) } : {}),
      tag_ids: values.tag_ids || [], images: (values.images || []).map(url => url.trim()).filter(Boolean),
    };
    if (post.completed_at) {
      delete data.recruitment_count; delete data.is_recruiting;
    }
    // 未编辑截止设置时保留原来的到期时间，避免保存其它字段意外延长帖子。
    if (values.is_recruiting && !post.completed_at && (values.expire_type !== post.expire_type || (values.expire_type === 'duration' && values.expire_value !== post.expire_value))) {
      data.expire_type = values.expire_type;
      if (values.expire_type === 'duration') data.expire_value = values.expire_value;
    }
    if (values.is_recruiting && !post.completed_at && values.expire_type === 'specific' && (values.expire_type !== post.expire_type || values.expire_time !== toLocalTime(post.expire_time))) {
      data.expire_type = 'specific'; data.expire_time = new Date(values.expire_time).toISOString();
    }
    // 已关闭/满员帖子维持现状时不提交开启招募标记。
    if (data.is_recruiting === post.is_recruiting) delete data.is_recruiting;
    if (data.images.length + files.length > 9) throw new Error('每个帖子最多 9 张图片');
    const result = await updatePost(postId, data);
    if (!result?.id) throw new Error('帖子保存响应无效');
    const failed = await uploadSelectedImages(postId, files, image => form.setFieldValue('images', [...(form.getFieldValue('images') || []), image.image_url]));
    setFiles(failed);
    if (failed.length) { message.warning('帖子内容已保存，部分图片上传失败，请重试保存'); return; }
    message.success('帖子已保存'); onSaved();
  });

  return <Form form={form} className="post-detail-editor post-form" layout="vertical" disabled={busy} scrollToFirstError onFinish={save} initialValues={{ post_type: 'general', location_visibility: 'hidden', ...post, expire_time: toLocalTime(post.expire_time), recruitment_count: post.recruitment_count || post.max_participants, tag_ids: (post.tags || []).map(tag => tag.id), images: (post.images || []).map(image => image.image_url) }}>
    <PostFormSection number="01" title={isRecruiting ? '招募内容' : '帖子内容'} description={isRecruiting ? '让大家一眼看懂你想找什么样的搭子' : '分享你想说的事'}>
      <Form.Item name="title" label="标题" rules={[{ required: true, whitespace: true, message: '请填写标题' }]}><Input maxLength={100} showCount placeholder={isRecruiting ? '例如：周六下午一起打羽毛球' : '写一个标题'} /></Form.Item>
      <Form.Item name="description" label="详细描述" rules={[{ required: true, whitespace: true, message: '请填写详细描述' }]}><Input.TextArea maxLength={2000} showCount autoSize={{ minRows: 5, maxRows: 12 }} placeholder={isRecruiting ? '介绍时间、活动内容以及你希望搭子了解的细节' : '分享你想说的内容'} /></Form.Item>
      <Form.Item name="tag_ids" label="标签" extra={isRecruiting ? '选择与招募内容相关的标签，方便其他人找到你。' : '选择与帖子内容相关的标签。'}><Select mode="multiple" maxTagCount="responsive" placeholder="选择标签（可选）" options={tags.map(tag => ({ value: tag.id, label: tag.name }))} optionFilterProp="label" /></Form.Item>
      {tagsError && <Alert className="post-form-tag-alert" type="warning" showIcon message={tagsError} />}
    </PostFormSection>
    <PostFormSection number="02" title="活动安排" description={isRecruiting ? '地点与截止时间可以帮助伙伴决定是否加入' : '修改招募状态，保留原有活动设置'}>
      <Form.Item name="is_recruiting" label="开放招募" valuePropName="checked" extra={post.completed_at ? '活动已完成，招募状态与人数不可修改。' : isRecruiting ? '开放后，其他用户可以通过评论申请加入，审批通过后自动加入帖子群聊。' : '关闭后，帖子仅用于分享交流，不接受招募申请。'}><Switch disabled={busy || Boolean(post.completed_at) || post.status !== 'active' || new Date(post.expire_time) <= new Date()} /></Form.Item>
      {isRecruiting && <>
        <Form.Item name="expire_type" label="招募有效期"><Radio.Group disabled={Boolean(post.completed_at)} optionType="button" buttonStyle="solid" options={[{ value: 'duration', label: '持续时长' }, { value: 'specific', label: '指定截止时间' }]} /></Form.Item>
        <Form.Item noStyle shouldUpdate={(previous, current) => previous.expire_type !== current.expire_type}>
          {({ getFieldValue }) => getFieldValue('expire_type') === 'specific'
            ? <Form.Item name="expire_time" label="截止时间" rules={[{ required: true }, { validator: (_, value) => value === toLocalTime(post.expire_time) || new Date(value).getTime() > Date.now() ? Promise.resolve() : Promise.reject(new Error('请选择未来时间')) }]}><Input disabled={Boolean(post.completed_at)} type="datetime-local" /></Form.Item>
            : <Form.Item name="expire_value" label="持续时长（小时）" extra="修改小时数会从保存时重新计算截止时间。" rules={[{ required: true }]}><InputNumber disabled={Boolean(post.completed_at)} min={1} max={8760} /></Form.Item>}
        </Form.Item>
        <Form.Item name="recruitment_count" label="招募人数" extra="填写你希望招募的伙伴人数。" rules={[{ required: true }]}><InputNumber disabled={Boolean(post.completed_at)} min={Math.max(1, post.approved_participant_count || 0)} /></Form.Item>
        <PostOptions form={form} publishLayout disabled={busy} onBusyChange={setMapBusy} locked={Boolean(post.completed_at || post.approved_participant_count)} />
      </>}
    </PostFormSection>
    <PostFormSection number="03" title="补充图片" description={isRecruiting ? '上传图片，让活动更直观' : '上传图片，让内容更直观'}>
      <Form.List name="images">{(fields, { remove, move }) => fields.length > 0 && <div className="post-detail-editor-images"><h3>已上传图片</h3>{fields.map((field, index) => <div className="post-detail-editor-image-row" key={field.key}>
        <img src={links[index]} alt={`帖子配图 ${index + 1}`} />
        <Form.Item name={field.name} hidden><Input /></Form.Item>
        <Button disabled={busy || index === 0} onClick={() => move(index, index - 1)}>前移</Button><Button danger disabled={busy} onClick={() => remove(field.name)}>移除</Button>
      </div>)}</div>}</Form.List>
      <div className="post-form-image-fields">
        <ImageQuota fetchQuota={fetchPostImageQuota} selectedFiles={files} showAccountQuota={false} />
        <ImagePicker files={files} onChange={setFiles} maxCount={Math.max(0, 9 - links.filter(Boolean).length)} disabled={busy} />
      </div>
    </PostFormSection>
    <div className="post-form-actions"><Button disabled={busy} onClick={onCancel}>取消</Button><Button type="primary" htmlType="submit" loading={busy} disabled={mapBusy}>保存修改</Button></div>
  </Form>;
};
export default PostEditor;
